import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Icons
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Stats {
  total_expense: number;
  total_paid: number;
  outstanding: number;
  monthly: number;
  monthly_paid: number;
}

interface TrendRow { month: string; total: number; }
interface CategoryRow { category: string; total: number; }
interface RecentExpense {
  id: number;
  date: string;
  description: string;
  category: string;
  amount: number;
  hst: number;
  total: number;
  status: string;
  type: string;
}
interface Category { id: number; category_name: string; }

interface AllExpense {
  id: number;
  date: string;
  description: string;
  category: number | string;
  amount: number;
  hst: number;
  total: number;
  type: string;
  status: string;
  is_installment: boolean | number | string;
  paid_amount: number;
  due_amount: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (v: number) =>
  "$" + v.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusChip = (s: string) => {
  const paid = s === "Paid";
  return (
    <Chip
      label={s}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: 10,
        height: 20,
        bgcolor: paid ? "#dcfce7" : "#fee2e2",
        color: paid ? "#15803d" : "#dc2626",
        border: `1px solid ${paid ? "#bbf7d0" : "#fecaca"}`,
      }}
    />
  );
};

// ── Stat Card ─────────────────────────────────────────────────────────────────

const StatCard = ({
  icon,
  label,
  value,
  sub,
  gradient,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  gradient: string;
}) => (
  <Box
    sx={{
      background: gradient,
      borderRadius: 3,
      p: 2.5,
      color: "#fff",
      position: "relative",
      overflow: "hidden",
      height: "100%",
      minHeight: 110,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        right: -10,
        top: -10,
        width: 70,
        height: 70,
        borderRadius: "50%",
        bgcolor: "rgba(255,255,255,0.1)",
      }}
    />
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.75)", textTransform: "uppercase", letterSpacing: 0.6, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{value}</Typography>
        {sub && (
          <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.65)", mt: 0.5 }}>{sub}</Typography>
        )}
      </Box>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: "rgba(255,255,255,0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    </Box>
  </Box>
);

// ── Main Component ────────────────────────────────────────────────────────────

const ExpenseDashboard: React.FC = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const currentYear = dayjs().year();

  const [stats, setStats] = useState<Stats | null>(null);
  const [trend, setTrend] = useState<TrendRow[]>([]);
  const [catBreakdown, setCatBreakdown] = useState<CategoryRow[]>([]);
  const [recent, setRecent] = useState<RecentExpense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allExpenses, setAllExpenses] = useState<AllExpense[]>([]);
  const [year, setYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<string>(dayjs().format("YYYY-MM"));
  const [loading, setLoading] = useState(true);

  const catMap = useMemo(
    () => categories.reduce((m, c) => { m[c.id] = c.category_name; return m; }, {} as Record<number, string>),
    [categories]
  );

  // ── Fetch all dashboard data ─────────────────────────────────────────────────
  const fetchAll = async (yr: number, month: string) => {
    setLoading(true);
    try {
      const [statsRes, trendRes, catRes, recentRes, categoriesRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/expenses/stats?month=${month}`),
        axios.get(`${API_URL}/expenses/yearly-trend?year=${yr}`),
        axios.get(`${API_URL}/expenses/category-breakdown?year=${yr}`),
        axios.get(`${API_URL}/expenses/recent`),
        axios.get(`${API_URL}/view-expense-category`),
        axios.get(`${API_URL}/view-expenses`),
      ]);
      setStats(statsRes.data.data);
      setTrend(trendRes.data.data ?? []);
      setCatBreakdown(catRes.data.data ?? []);
      setRecent(recentRes.data.data ?? []);
      setCategories(categoriesRes.data ?? []);
      const rawAll = allRes.data.data ?? allRes.data ?? [];
      setAllExpenses(rawAll);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(year, selectedMonth); }, [year, selectedMonth]);

  // ── Chart options ──────────────────────────────────────────────────────────

  const lineChartOptions = useMemo<Highcharts.Options>(() => ({
    chart: { type: "area", height: 260, backgroundColor: "#fff", style: { fontFamily: "inherit" } },
    title: { text: undefined },
    xAxis: {
      categories: trend.map((t) => t.month),
      labels: { style: { fontSize: "11px", color: "#64748b" } },
      lineColor: "#f1f5f9",
      tickColor: "transparent",
    },
    yAxis: {
      title: { text: undefined },
      labels: {
        formatter() { return "$" + Highcharts.numberFormat((this.value as number), 0, ".", ","); },
        style: { fontSize: "11px", color: "#64748b" },
      },
      gridLineColor: "#f1f5f9",
    },
    series: [{
      name: "Total Expense",
      type: "area",
      data: trend.map((t) => t.total),
      color: "#2f3891",
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [[0, "rgba(99,102,241,0.3)"], [1, "rgba(99,102,241,0.0)"]],
      },
      lineWidth: 2.5,
      marker: { radius: 4, fillColor: "#2f3891", lineColor: "#fff", lineWidth: 2 },
    }],
    tooltip: {
      formatter() { return `<b>${this.x}</b><br/>$${Highcharts.numberFormat(this.y as number, 2)}`; },
      backgroundColor: "#1e293b",
      style: { color: "#fff" },
      borderRadius: 8,
    },
    legend: { enabled: false },
    credits: { enabled: false },
  }), [trend]);

  const barChartOptions = useMemo<Highcharts.Options>(() => ({
    chart: { type: "bar", height: 260, backgroundColor: "#fff", style: { fontFamily: "inherit" } },
    title: { text: undefined },
    xAxis: {
      categories: catBreakdown.map((c) => c.category),
      labels: { style: { fontSize: "11px", color: "#64748b" } },
      lineColor: "transparent",
      tickColor: "transparent",
    },
    yAxis: {
      title: { text: undefined },
      labels: {
        formatter() { return "$" + Highcharts.numberFormat((this.value as number), 0, ".", ","); },
        style: { fontSize: "11px", color: "#64748b" },
      },
      gridLineColor: "#f1f5f9",
    },
    series: [{
      name: "Amount",
      type: "bar",
      data: catBreakdown.map((c) => ({
        y: c.total,
        color: "#2f3891",
      })),
      colorByPoint: true,
      colors: ["#2f3891","#2f3891","#a78bfa","#c4b5fd","#ddd6fe","#10b981","#34d399","#f59e0b","#ef4444","#0ea5e9"],
      dataLabels: {
        enabled: true,
        formatter() { return "$" + Highcharts.numberFormat(this.y as number, 0, ".", ","); },
        style: { fontSize: "10px", fontWeight: "600", color: "#475569" },
      },
    }],
    tooltip: {
      formatter() { return `<b>${this.x}</b><br/>$${Highcharts.numberFormat(this.y as number, 2)}`; },
      backgroundColor: "#1e293b",
      style: { color: "#fff" },
      borderRadius: 8,
    },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: { bar: { borderRadius: 4, pointPadding: 0.1, groupPadding: 0.05 } },
  }), [catBreakdown]);

  // ── Type colour map ───────────────────────────────────────────────────────
  const TYPE_COLORS: Record<string, string> = {
    "Material": "#0ea5e9",
    "Labour": "#10b981",
    "Other": "#f59e0b",
    "Material+Labour": "#2f3891",
  };

  // ── Group expenses by category ─────────────────────────────────────────────
  const categoryGroups = useMemo(() => {
    const groups: Record<string, AllExpense[]> = {};
    allExpenses.forEach((exp) => {
      const catId = String(exp.category);
      if (!groups[catId]) groups[catId] = [];
      groups[catId].push(exp);
    });
    return groups;
  }, [allExpenses]);

  // ── Installment expenses ───────────────────────────────────────────────────
  const installmentExpenses = useMemo(
    () => allExpenses.filter((e) => e.is_installment === true || e.is_installment === 1 || e.is_installment === "1"),
    [allExpenses]
  );

  // ── Build Highcharts pie options for a category ────────────────────────────
  const buildPieOptions = (expenses: AllExpense[]): Highcharts.Options => {
    const typeTotals: Record<string, number> = {};
    expenses.forEach((e) => {
      const t = e.type || "Other";
      typeTotals[t] = (typeTotals[t] ?? 0) + parseFloat(String(e.total || 0));
    });
    const data = Object.entries(typeTotals).map(([name, y]) => ({
      name,
      y,
      color: TYPE_COLORS[name] ?? "#94a3b8",
    }));
    return {
      chart: { type: "pie", height: 160, backgroundColor: "transparent", margin: [0, 0, 0, 0], spacing: [0, 0, 0, 0] },
      title: { text: undefined },
      tooltip: {
        formatter() { return `<b>${this.point.name}</b><br/>$${Highcharts.numberFormat(this.y as number, 2)} (${Highcharts.numberFormat(this.percentage as number, 1)}%)`; },
        backgroundColor: "#1e293b", style: { color: "#fff" }, borderRadius: 8,
      },
      plotOptions: {
        pie: {
          dataLabels: { enabled: false },
          showInLegend: true,
          size: "85%",
          innerSize: "55%",
          borderWidth: 2,
          borderColor: "#fff",
        },
      },
      legend: {
        enabled: true,
        layout: "vertical",
        align: "right",
        verticalAlign: "middle",
        itemStyle: { fontSize: "10px", fontWeight: "600", color: "#475569" },
        symbolRadius: 4,
        symbolHeight: 10,
        symbolWidth: 10,
      },
      series: [{ type: "pie", name: "Amount", data }],
      credits: { enabled: false },
    };
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>

      {/* ── Page Header ── */}
      <Box sx={{
        background: "linear-gradient(135deg,#2f3891 0%,#2f3891 100%)",
        borderRadius: 3, px: 3, py: 2.5, mb: 3,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TrendingDownIcon sx={{ color: "#fff", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 18, lineHeight: 1 }}>Expense Dashboard</Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 12, mt: 0.4 }}>
              Financial overview · {dayjs().format("MMMM YYYY")}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <DatePicker
            views={["month"]}
            openTo="month"
            value={dayjs(selectedMonth + "-01")}
            minDate={dayjs().startOf("year")}
            maxDate={dayjs().endOf("year")}
            onChange={(val: Dayjs | null) => { if (val && val.isValid()) setSelectedMonth(val.format("YYYY-MM")); }}
            format="MMM YYYY"
            slots={{ openPickerIcon: CalendarMonthOutlinedIcon }}
            slotProps={{
              textField: {
                size: "small",
                placeholder: "Select month",
                sx: {
                  width: 170,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5,
                    bgcolor: "rgba(255,255,255,0.15)",
                    backdropFilter: "blur(4px)",
                    height: 38,
                    "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
                    "&.Mui-focused fieldset": { borderColor: "#fff" },
                  },
                  "& input": { color: "#fff", fontSize: 13, fontWeight: 600, letterSpacing: 0.3 },
                  "& .MuiIconButton-root": { color: "rgba(255,255,255,0.7)" },
                },
              },
              popper: {
                sx: {
                  "& .MuiPaper-root": {
                    borderRadius: 3,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                    border: "1px solid #e2e8f0",
                    mt: 0.5,
                  },
                  "& .MuiPickersMonth-monthButton.Mui-selected": {
                    bgcolor: "#2f3891 !important",
                    color: "#fff !important",
                    fontWeight: 700,
                  },
                  "& .MuiPickersMonth-monthButton:hover": {
                    bgcolor: "#eef2ff",
                  },
                  "& .MuiPickersCalendarHeader-label": {
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#1e293b",
                    pointerEvents: "none",
                  },
                  "& .MuiPickersArrowSwitcher-button": {
                    borderRadius: 1.5,
                    color: "#475569",
                    "&:hover": { bgcolor: "#f1f5f9" },
                  },
                },
              },
            }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AssessmentOutlinedIcon />}
            onClick={() => navigate("/expense/reports")}
            sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)", textTransform: "none", fontWeight: 600, fontSize: 12, "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}
          >
            Reports
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => navigate("/expense/create")}
            sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", textTransform: "none", fontWeight: 700, fontSize: 12, "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}
          >
            Add Expense
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress sx={{ color: "#2f3891" }} />
        </Box>
      ) : (
        <>
          {/* ── Stat Cards ── */}
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <StatCard
                icon={<TrendingDownIcon sx={{ color: "#fff", fontSize: 22 }} />}
                label="Total Expense"
                value={fmt(stats?.total_expense ?? 0)}
                gradient="linear-gradient(135deg,#2f3891,#2f3891)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <StatCard
                icon={<CheckCircleOutlineIcon sx={{ color: "#fff", fontSize: 22 }} />}
                label="Total Paid"
                value={fmt(stats?.total_paid ?? 0)}
                gradient="linear-gradient(135deg,#10b981,#34d399)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <StatCard
                icon={<WarningAmberIcon sx={{ color: "#fff", fontSize: 22 }} />}
                label="Outstanding"
                value={fmt(stats?.outstanding ?? 0)}
                gradient="linear-gradient(135deg,#f59e0b,#fbbf24)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
              <StatCard
                icon={<CalendarMonthOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />}
                label="This Month"
                value={fmt(stats?.monthly ?? 0)}
                sub={`Paid: ${fmt(stats?.monthly_paid ?? 0)}`}
                gradient="linear-gradient(135deg,#0ea5e9,#38bdf8)"
              />
            </Grid>
          </Grid>

          {/* ── Charts ── */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Year Selector */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: "#64748b", mr: 1 }}>Year:</Typography>
                <Select
                  size="small"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  sx={{ fontSize: 12, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                >
                  {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                    <MenuItem key={y} value={y} sx={{ fontSize: 12 }}>{y}</MenuItem>
                  ))}
                </Select>
              </Box>
            </Grid>

            {/* Line chart */}
            <Grid item xs={12} md={7}>
              <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", p: 2.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
                  Yearly Expense Trend
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mb: 1.5 }}>Monthly totals for {year}</Typography>
                <HighchartsReact highcharts={Highcharts} options={lineChartOptions} />
              </Box>
            </Grid>

            {/* Bar chart */}
            <Grid item xs={12} md={5}>
              <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", p: 2.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
                  Expense by Category
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mb: 1.5 }}>Top categories for {year}</Typography>
                {catBreakdown.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>No data for {year}</Typography>
                  </Box>
                ) : (
                  <HighchartsReact highcharts={Highcharts} options={barChartOptions} />
                )}
              </Box>
            </Grid>
          </Grid>

          {/* ── Category-Wise Pie Charts ── */}
          {Object.keys(categoryGroups).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1.5,
                  background: "linear-gradient(135deg,#2f3891,#2f3891)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <AssessmentOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Category Breakdown</Typography>
              </Box>
              <Grid container spacing={2}>
                {Object.entries(categoryGroups).map(([catId, exps]) => {
                  const catName = catMap[Number(catId)] ?? `Category #${catId}`;
                  const totalAmt = exps.reduce((s, e) => s + parseFloat(String(e.total || 0)), 0);
                  const totalHst = exps.reduce((s, e) => s + parseFloat(String(e.hst || 0)), 0);
                  const overallTotal = allExpenses.reduce((s, e) => s + parseFloat(String(e.total || 0)), 0);
                  const pct = overallTotal > 0 ? ((totalAmt / overallTotal) * 100).toFixed(1) : "0.0";
                  return (
                    <Grid item xs={12} md={6} lg={4} key={catId}>
                      <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
                        {/* Card header */}
                        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e293b", flex: 1, mr: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {catName}
                            </Typography>
                            <Chip
                              label={`${pct}%`}
                              size="small"
                              sx={{ bgcolor: "#ede9fe", color: "#2f3891", fontWeight: 700, fontSize: 10, height: 20 }}
                            />
                          </Box>
                          <Typography sx={{ fontSize: 11, color: "#64748b" }}>{exps.length} expense{exps.length !== 1 ? "s" : ""}</Typography>
                        </Box>

                        {/* Pie chart */}
                        <Box sx={{ px: 1, pt: 0.5 }}>
                          <HighchartsReact highcharts={Highcharts} options={buildPieOptions(exps)} />
                        </Box>

                        {/* Expense rows */}
                        <Box sx={{ flex: 1, overflowY: "auto", maxHeight: 160, px: 2, pb: 1 }}>
                          {exps.slice(0, 8).map((e) => (
                            <Box key={e.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.6, borderBottom: "1px solid #f8fafc" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, mr: 1, overflow: "hidden" }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: TYPE_COLORS[e.type] ?? "#94a3b8", flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 11, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {e.description || "—"}
                                </Typography>
                              </Box>
                              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
                                ${parseFloat(String(e.total || 0)).toFixed(2)}
                              </Typography>
                            </Box>
                          ))}
                          {exps.length > 8 && (
                            <Typography sx={{ fontSize: 10, color: "#94a3b8", textAlign: "center", py: 0.5 }}>+{exps.length - 8} more</Typography>
                          )}
                        </Box>

                        {/* Footer totals */}
                        <Box sx={{ bgcolor: "#f8fafc", px: 2.5, py: 1.2, display: "flex", justifyContent: "space-between", borderTop: "1px solid #e2e8f0" }}>
                          <Box>
                            <Typography sx={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>Total Expense</Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#1e293b" }}>{fmt(totalAmt)}</Typography>
                          </Box>
                          <Box sx={{ textAlign: "right" }}>
                            <Typography sx={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>Total HST</Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#f59e0b" }}>{fmt(totalHst)}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* ── Installment Expenses ── */}
          {installmentExpenses.length > 0 && (
            <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", mb: 3 }}>
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: 2.5, py: 1.75, borderBottom: "1px solid #f1f5f9",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.5,
                    background: "linear-gradient(135deg,#f59e0b,#fbbf24)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <CalendarMonthOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>Installment Expenses</Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{installmentExpenses.length} instalment record{installmentExpenses.length !== 1 ? "s" : ""}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 3, textAlign: "right" }}>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>Total Paid</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#10b981" }}>
                      {fmt(installmentExpenses.reduce((s, e) => s + parseFloat(String(e.paid_amount || 0)), 0))}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>Total Due</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#ef4444" }}>
                      {fmt(installmentExpenses.reduce((s, e) => s + parseFloat(String(e.due_amount || 0)), 0))}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#fffbeb" }}>
                      {["Date", "Description", "Category", "Type", "Total Amount", "Paid", "Due", "Status"].map((h) => (
                        <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, borderBottom: "1px solid #e2e8f0" }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {installmentExpenses.map((exp) => (
                      <TableRow key={exp.id} hover sx={{ "& td": { borderBottom: "1px solid #f1f5f9" } }}>
                        <TableCell sx={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                          {dayjs(exp.date).format("MMM D, YYYY")}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#1e293b", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {exp.description}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#475569" }}>
                          {catMap[Number(exp.category)] ?? `#${exp.category}`}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: TYPE_COLORS[exp.type] ?? "#94a3b8" }} />
                            <Typography sx={{ fontSize: 12, color: "#475569" }}>{exp.type || "—"}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
                          ${parseFloat(String(exp.total || 0)).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "#10b981", whiteSpace: "nowrap" }}>
                          ${parseFloat(String(exp.paid_amount || 0)).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "#ef4444", whiteSpace: "nowrap" }}>
                          ${parseFloat(String(exp.due_amount || 0)).toFixed(2)}
                        </TableCell>
                        <TableCell>{statusChip(exp.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ── Recent Expenses ── */}
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: 2.5, py: 1.75, borderBottom: "1px solid #f1f5f9",
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1.5,
                  background: "linear-gradient(135deg,#2f3891,#2f3891)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <ReceiptLongOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>Recent Expenses</Typography>
              </Box>
              <Button
                size="small"
                onClick={() => navigate("/expense/list")}
                sx={{ textTransform: "none", fontSize: 12, color: "#2f3891", fontWeight: 600 }}
              >
                View All →
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    {["Date", "Description", "Category", "Type", "Amount", "HST", "Total", "Status"].map((h) => (
                      <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, borderBottom: "1px solid #e2e8f0" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "#94a3b8", fontSize: 13 }}>
                        No expenses yet. <Button size="small" onClick={() => navigate("/expense/create")} sx={{ textTransform: "none", color: "#2f3891" }}>Add one</Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recent.map((exp) => (
                      <TableRow key={exp.id} hover sx={{ "& td": { borderBottom: "1px solid #f1f5f9" } }}>
                        <TableCell sx={{ fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                          {dayjs(exp.date).format("MMM D, YYYY")}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#1e293b", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {exp.description}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#475569" }}>
                          {catMap[Number(exp.category)] ?? `#${exp.category}`}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#475569" }}>{exp.type ?? "—"}</TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#1e293b", fontWeight: 600, whiteSpace: "nowrap" }}>
                          ${parseFloat(String(exp.amount)).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
                          ${parseFloat(String(exp.hst)).toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
                          ${parseFloat(String(exp.total)).toFixed(2)}
                        </TableCell>
                        <TableCell>{statusChip(exp.status)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ExpenseDashboard;
